/**
 * Magic-link landing page.
 *
 * The user arrives here from the verification email at
 *   /verify-email?token=xxx
 *
 * We POST the token to /auth/verify, then route them onward:
 *   - lifetime/active subscriber → /dashboard
 *   - otherwise                  → /paywall
 *
 * If the link is invalid or expired we surface the error and offer to resend.
 */
import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import MarketingHeader from "@/components/MarketingHeader";

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { checkAuth, refreshBilling } = useAuth();
  const [status, setStatus] = useState("checking"); // checking | ok | error
  const [errorMsg, setErrorMsg] = useState("");
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    const token = params.get("token");
    if (!token) {
      setStatus("error");
      setErrorMsg("Missing verification token.");
      return;
    }
    (async () => {
      try {
        await api.post("/auth/verify", { token });
        // Refresh the cached user/billing so the rest of the app sees email_verified=true.
        await checkAuth();
        const bs = await refreshBilling();
        setStatus("ok");
        // Brief pause so the user sees the success state.
        setTimeout(() => {
          if (bs?.has_access) {
            navigate("/dashboard", { replace: true });
          } else {
            navigate("/paywall", { replace: true });
          }
        }, 1200);
      } catch (e) {
        setStatus("error");
        const detail = e?.response?.data?.detail;
        setErrorMsg(
          typeof detail === "string"
            ? detail
            : "Could not verify this link.",
        );
      }
    })();
  }, [params, navigate, checkAuth, refreshBilling]);

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex flex-col">
      <MarketingHeader showSubscribe={false} />
      <div className="flex-1 flex items-center justify-center p-6">
        <div
          className="w-full max-w-md text-center"
          data-testid="verify-email-page"
        >
          {status === "checking" && (
            <>
              <Loader2 className="w-10 h-10 animate-spin mx-auto text-neutral-400" strokeWidth={1.4} />
              <div className="mt-6 text-[10px] uppercase tracking-[0.3em] font-mono text-neutral-500">
                Confirming your email
              </div>
              <p className="mt-3 text-neutral-300 text-sm">One moment…</p>
            </>
          )}
          {status === "ok" && (
            <>
              <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-400" strokeWidth={1.4} />
              <h1 className="font-display text-2xl font-semibold mt-5">
                Email confirmed.
              </h1>
              <p className="mt-3 text-neutral-400 text-sm">
                Routing you to your account…
              </p>
            </>
          )}
          {status === "error" && (
            <>
              <AlertTriangle className="w-12 h-12 mx-auto text-amber-400" strokeWidth={1.4} />
              <h1 className="font-display text-2xl font-semibold mt-5">
                Link invalid or expired.
              </h1>
              <p className="mt-3 text-neutral-400 text-sm">{errorMsg}</p>
              <a
                href="/paywall"
                data-testid="verify-error-resend-link"
                className="mt-6 inline-block text-white text-sm underline"
              >
                Go to your account to request a new link
              </a>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
